/**
 * usePlannerDay — planner items + medication occurrences for one local date.
 *
 * Sync truthfulness (spec §8.3, contract §3/§8):
 * - Optimistic UI ONLY with rollback on a failed cloud write: a failed write
 *   while online restores the previous state and marks the item
 *   `sync_failed` — it never pretends to be in the cloud.
 * - While offline the optimistic change is kept, the write is enqueued in
 *   the shared offline queue ('rust-sync-queue') and the item shows the
 *   honest `sync_queued` state ("Wacht op synchronisatie").
 * - SUPABASE_NOT_CONFIGURED surfaces as a typed AppError; the page renders
 *   ErrorState. No local write is ever presented as a cloud save.
 * - Medication occurrences are read-only and render visually distinct.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { toAppError } from '@/lib/supabase/errors'
import { repositories } from '@/services/repositories'
import { registerSyncHandler, useSync } from '@/services/sync'
import { enqueueWrite, loadQueue, removeWrite } from '@/services/sync/queue'
import type {
  AppError,
  MedicationOccurrence,
  NewPlannerItem,
  PlannerItem,
  SyncSaveState,
} from '@/types'

export type PlannerLoadStatus = 'loading' | 'ready' | 'error'

export type PlannerWriteResult =
  | { ok: true }
  | { ok: false; error: AppError; state: SyncSaveState }

type PlannerUpdatePatch = Partial<
  Pick<PlannerItem, 'date' | 'startMinute' | 'durationMinutes' | 'notes' | 'activityName' | 'category' | 'color' | 'iconId'>
>

const TEMP_ID_PREFIX = 'tmp-planner-'

function newTempId(): string {
  const uuid =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  return `${TEMP_ID_PREFIX}${uuid}`
}

function isTempId(id: string): boolean {
  return id.startsWith(TEMP_ID_PREFIX)
}

// Queue executors survive reloads; registered once at module scope.
registerSyncHandler('planner.create', async (payload) => {
  await repositories.planner.create(payload as NewPlannerItem)
})
registerSyncHandler('planner.update', async (payload) => {
  const { id, patch } = payload as { id: string; patch: PlannerUpdatePatch }
  await repositories.planner.update(id, patch)
})
registerSyncHandler('planner.remove', async (payload) => {
  const { id } = payload as { id: string }
  await repositories.planner.remove(id)
})

/** Removes queued writes for a dedupe key (e.g. a discarded pending create). */
function removeQueuedWritesByDedupeKey(dedupeKey: string): void {
  for (const entry of loadQueue().entries) {
    if (entry.dedupeKey === dedupeKey) removeWrite(entry.id)
  }
}

/**
 * Enqueues an offline write. Returns null on success or the typed AppError
 * (e.g. SYNC_QUEUE_FULL) so callers can roll back the optimistic change
 * instead of leaving an untracked local edit.
 */
function tryEnqueue(input: { kind: string; payload: unknown; dedupeKey?: string }): AppError | null {
  try {
    enqueueWrite(input)
    return null
  } catch (queueError) {
    return toAppError(queueError, 'browser')
  }
}

export function usePlannerDay(date: string) {
  const { online, pendingWrites } = useSync()
  const [items, setItems] = useState<PlannerItem[]>([])
  const [occurrences, setOccurrences] = useState<MedicationOccurrence[]>([])
  const [status, setStatus] = useState<PlannerLoadStatus>('loading')
  const [error, setError] = useState<AppError | null>(null)
  const [syncStates, setSyncStates] = useState<Record<string, SyncSaveState>>({})

  const setItemState = useCallback((itemId: string, state: SyncSaveState) => {
    setSyncStates((previous) => ({ ...previous, [itemId]: state }))
  }, [])

  const clearItemState = useCallback((itemId: string) => {
    setSyncStates((previous) => {
      const next = { ...previous }
      delete next[itemId]
      return next
    })
  }, [])

  const refresh = useCallback(async () => {
    setStatus('loading')
    setError(null)
    try {
      const [list, medicationOccurrences] = await Promise.all([
        repositories.planner.listForDate(date),
        // Occurrences are a read-only enhancement; a failure here must never
        // block the planner itself.
        repositories.medication.listOccurrences(date, date).catch(() => [] as MedicationOccurrence[]),
      ])
      setItems(list)
      setOccurrences(medicationOccurrences)
      setSyncStates((previous) => {
        const next: Record<string, SyncSaveState> = {}
        for (const item of list) {
          // Keep an explicit failure visible instead of silently upgrading it.
          next[item.id] = previous[item.id] === 'sync_failed' ? 'sync_failed' : 'cloud_saved'
        }
        return next
      })
      setStatus('ready')
    } catch (loadError) {
      setError(toAppError(loadError, 'database'))
      setStatus('error')
    }
  }, [date])

  useEffect(() => {
    void refresh()
  }, [refresh])

  // Reconcile pending optimistic items once the offline queue has drained.
  const hasTempItems = items.some((item) => isTempId(item.id))
  const previousPendingRef = useRef(pendingWrites)
  useEffect(() => {
    const hadPending = previousPendingRef.current > 0
    previousPendingRef.current = pendingWrites
    if (online && hadPending && pendingWrites === 0 && hasTempItems) {
      void refresh()
    }
  }, [online, pendingWrites, hasTempItems, refresh])

  /**
   * Places a new item. Optimistic insert with rollback on failure; when
   * offline the item stays visible as `sync_queued` and the write is queued.
   */
  const placeItem = useCallback(
    async (draft: NewPlannerItem): Promise<PlannerWriteResult> => {
      const now = new Date().toISOString()
      const optimistic: PlannerItem = {
        id: newTempId(),
        userId: '',
        createdAt: now,
        updatedAt: now,
        ...draft,
      }
      setItems((previous) => [...previous, optimistic])
      setItemState(optimistic.id, 'syncing')

      if (!online) {
        const queueError = tryEnqueue({ kind: 'planner.create', payload: draft, dedupeKey: optimistic.id })
        if (queueError) {
          setItems((previous) => previous.filter((item) => item.id !== optimistic.id))
          clearItemState(optimistic.id)
          return { ok: false, error: queueError, state: 'sync_failed' }
        }
        setItemState(optimistic.id, 'sync_queued')
        return { ok: true }
      }

      try {
        const saved = await repositories.planner.create(draft)
        setItems((previous) => previous.map((item) => (item.id === optimistic.id ? saved : item)))
        setItemState(saved.id, 'cloud_saved')
        clearItemState(optimistic.id)
        return { ok: true }
      } catch (writeError) {
        const appError = toAppError(writeError, 'database')
        // Rollback: remove the optimistic item, never claim a cloud save.
        setItems((previous) => previous.filter((item) => item.id !== optimistic.id))
        clearItemState(optimistic.id)
        return { ok: false, error: appError, state: 'sync_failed' }
      }
    },
    [online, setItemState, clearItemState],
  )

  /**
   * Updates an item (edit / move / resize / note). Optimistic patch with
   * rollback to the exact previous server state on failure.
   */
  const updateItem = useCallback(
    async (itemId: string, patch: PlannerUpdatePatch): Promise<PlannerWriteResult> => {
      const previous = items.find((item) => item.id === itemId)
      if (!previous) return { ok: false, error: toAppError(new Error('item not found'), 'validation'), state: 'sync_failed' }

      const optimistic: PlannerItem = { ...previous, ...patch, updatedAt: new Date().toISOString() }
      setItems((current) => current.map((item) => (item.id === itemId ? optimistic : item)))
      setItemState(itemId, 'syncing')

      // A not-yet-synced create only exists locally: fold the patch into the
      // queued payload and keep the honest queued state.
      if (isTempId(itemId)) {
        const { id: _id, userId: _userId, createdAt: _createdAt, updatedAt: _updatedAt, ...draft } = optimistic
        const queueError = tryEnqueue({ kind: 'planner.create', payload: draft, dedupeKey: itemId })
        if (queueError) {
          setItems((current) => current.map((item) => (item.id === itemId ? previous : item)))
          setItemState(itemId, 'sync_failed')
          return { ok: false, error: queueError, state: 'sync_failed' }
        }
        setItemState(itemId, 'sync_queued')
        return { ok: true }
      }

      if (!online) {
        const queueError = tryEnqueue({
          kind: 'planner.update',
          payload: { id: itemId, patch },
          dedupeKey: `planner-update-${itemId}`,
        })
        if (queueError) {
          setItems((current) => current.map((item) => (item.id === itemId ? previous : item)))
          setItemState(itemId, 'sync_failed')
          return { ok: false, error: queueError, state: 'sync_failed' }
        }
        setItemState(itemId, 'sync_queued')
        return { ok: true }
      }

      try {
        const saved = await repositories.planner.update(itemId, patch)
        setItems((current) => current.map((item) => (item.id === itemId ? saved : item)))
        setItemState(itemId, 'cloud_saved')
        return { ok: true }
      } catch (writeError) {
        const appError = toAppError(writeError, 'database')
        // Rollback to the previous server-confirmed state.
        setItems((current) => current.map((item) => (item.id === itemId ? previous : item)))
        setItemState(itemId, 'sync_failed')
        return { ok: false, error: appError, state: 'sync_failed' }
      }
    },
    [items, online, setItemState],
  )

  /** Deletes an item with rollback (restore) on a failed cloud write. */
  const removeItem = useCallback(
    async (itemId: string): Promise<PlannerWriteResult> => {
      const previous = items.find((item) => item.id === itemId)
      if (!previous) return { ok: false, error: toAppError(new Error('item not found'), 'validation'), state: 'sync_failed' }

      setItems((current) => current.filter((item) => item.id !== itemId))

      // Pending creates never reached the server: drop the queued write and
      // the local copy — nothing to delete remotely.
      if (isTempId(itemId)) {
        removeQueuedWritesByDedupeKey(itemId)
        clearItemState(itemId)
        return { ok: true }
      }

      if (!online) {
        const queueError = tryEnqueue({
          kind: 'planner.remove',
          payload: { id: itemId },
          dedupeKey: `planner-remove-${itemId}`,
        })
        if (queueError) {
          setItems((current) => [...current, previous])
          setItemState(itemId, 'sync_failed')
          return { ok: false, error: queueError, state: 'sync_failed' }
        }
        clearItemState(itemId)
        return { ok: true }
      }

      try {
        await repositories.planner.remove(itemId)
        clearItemState(itemId)
        return { ok: true }
      } catch (writeError) {
        const appError = toAppError(writeError, 'database')
        // Rollback: restore the item and report the failure honestly.
        setItems((current) => [...current, previous])
        setItemState(itemId, 'sync_failed')
        return { ok: false, error: appError, state: 'sync_failed' }
      }
    },
    [items, online, setItemState, clearItemState],
  )

  return {
    items,
    occurrences,
    status,
    error,
    syncStates,
    online,
    pendingWrites,
    refresh,
    placeItem,
    updateItem,
    removeItem,
  }
}
